import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Box, Column, Divider, DropdownMenu, IconButton, Row, TextButton, Typography } from 'seagull';
import { Kebab } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { getStatusLabel, invoiceLogicTextsPath } from '~/business-logic/invoiceLogic';
import { getStatusBadgeSkin } from '~/components/ClientInvoicesListView/ClientInvoicesListView.utils';
import { getURLWithFullPageInvoice } from '~/components/FullPageInvoice/FullPageInvoice.utils';
import { InvoiceAccountingIntegrationStatus } from '~/components/InvoiceAccountingIntegrationStatus';
import { InvoiceActionsMenuItems } from '~/components/InvoiceActions/InvoiceActionsMenuItems';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { invoiceSummaryModel } from '~/models';
import { formatDateLong } from '~/models/time/selectors';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import styles from './InvoiceLine.module.scss';
export const GetInvoiceLineTID = (parentTestId)=>getTestIds(parentTestId);
export function InvoiceLine(param) {
    let { invoice, customerId, testId, statusLabel, showDivider, hideManagePaymentMethods = false } = param;
    const navigate = useNavigate();
    const { InvoiceLineTestIds: TID } = useMemo(()=>GetInvoiceLineTID(testId), [
        testId
    ]);
    const { t: billingOverviewTexts } = useLocaleKeys('pages.clientDetail.summary.billingOverview');
    const { t: invoiceLogicTexts } = useLocaleKeys(invoiceLogicTextsPath);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const statusBadgeLabel = getStatusLabel(invoiceSummaryModel.displayStatus(invoice), invoiceLogicTexts.displayedStatusLabels);
    const statusBadgeSkin = getStatusBadgeSkin(invoice);
    const relationshipId = invoiceSummaryModel.relationshipId(invoice);
    const openInvoice = ()=>navigate(getURLWithFullPageInvoice({
            invoiceId: invoice.id,
            relationshipId
        }));
    return /*#__PURE__*/ _jsx(Box, {
        testId: testId,
        className: styles.invoiceRow,
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: 8,
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "start",
                    justifyContent: "space-between",
                    children: [
                        /*#__PURE__*/ _jsxs(Column, {
                            gap: 4,
                            children: [
                                /*#__PURE__*/ _jsxs(Row, {
                                    gap: 8,
                                    alignItems: "center",
                                    children: [
                                        /*#__PURE__*/ _jsx(TextButton, {
                                            bi: {
                                                name: 'client_detail_invoice_open_number'
                                            },
                                            skin: "primary",
                                            variant: "paragraph2",
                                            weight: "light",
                                            underline: "none",
                                            onClick: openInvoice,
                                            children: invoice.formattedNumber
                                        }),
                                        /*#__PURE__*/ _jsx(Badge, {
                                            skin: statusBadgeSkin,
                                            size: "small",
                                            rounded: true,
                                            outlined: true,
                                            testId: TID.StatusBadge(),
                                            children: statusBadgeLabel
                                        }),
                                        showQboIntegration && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                                            testId: TID.Qbo(),
                                            invoiceIntegration: invoice.integration,
                                            accountingType: Accounting.QBO,
                                            iconSize: "xsmall"
                                        }),
                                        showXeroIntegration && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                                            testId: TID.Xero(),
                                            invoiceIntegration: invoice.xeroIntegration.getIntegration(),
                                            accountingType: Accounting.Xero,
                                            iconSize: "xsmall"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph3",
                                    skin: "secondary",
                                    children: billingOverviewTexts.issueDate({
                                        date: formatDateLong(invoice.rawIssuedOn)
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(DropdownMenu, {
                            gap: 0,
                            target: /*#__PURE__*/ _jsx(IconButton, {
                                bi: {
                                    name: 'client_detail_invoice_menu'
                                },
                                testId: TID.Menu(),
                                skin: "transparent",
                                className: styles.kebab,
                                children: /*#__PURE__*/ _jsx(Kebab, {
                                    size: 20
                                })
                            }),
                            children: /*#__PURE__*/ _jsx(InvoiceActionsMenuItems, {
                                invoice: invoice,
                                customerId: customerId,
                                testId: TID.Menu(),
                                hideManagePaymentMethods: hideManagePaymentMethods
                            })
                        })
                    ]
                }),
                showDivider && /*#__PURE__*/ _jsx(Divider, {
                    margin: 0
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    justifyContent: "space-between",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "normal",
                            children: statusLabel
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "normal",
                            children: invoice.totalAmount
                        })
                    ]
                })
            ]
        })
    });
}
